const THREAT_TYPES = {
  IMPOSSIBLE_TRAVEL: 'impossible_travel',
  KNOWN_BAD_IP: 'known_bad_ip',
  FAILURE_SPIKE: 'failure_spike',
  NEW_COUNTRY: 'new_country',
  TOR_VPN: 'tor_vpn',
  ANOMALOUS_TIME: 'anomalous_time',
  LEGACY_AUTH: 'legacy_auth',
  BRUTE_FORCE: 'brute_force',
  SUSPICIOUS_APP: 'suspicious_app',
};

const SEVERITY_MAP = {
  [THREAT_TYPES.IMPOSSIBLE_TRAVEL]: 'high',
  [THREAT_TYPES.KNOWN_BAD_IP]: 'critical',
  [THREAT_TYPES.FAILURE_SPIKE]: 'medium',
  [THREAT_TYPES.NEW_COUNTRY]: 'medium',
  [THREAT_TYPES.TOR_VPN]: 'high',
  [THREAT_TYPES.ANOMALOUS_TIME]: 'low',
  [THREAT_TYPES.LEGACY_AUTH]: 'medium',
  [THREAT_TYPES.BRUTE_FORCE]: 'critical',
  [THREAT_TYPES.SUSPICIOUS_APP]: 'low',
};

const EARTH_RADIUS_KM = 6371;

function haversineDistance(lat1, lon1, lat2, lon2) {
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const a = sinDLat * sinDLat +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    sinDLon * sinDLon;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

export async function detectThreats(logs, enrichments, existingThreats) {
  const threats = [];
  const existingKeys = new Set();
  for (const t of existingThreats) {
    existingKeys.add(`${t.logId}:${t.type}`);
  }

  const enrichMap = new Map();
  for (const e of enrichments) {
    if (e?.ip) enrichMap.set(e.ip, e);
  }

  for (const log of logs) {
    log._ts = new Date(log.timestamp).getTime();
  }

  const addThreat = (log, type, enrichment, details = {}) => {
    const key = `${log.id}:${type}`;
    if (existingKeys.has(key)) return;
    existingKeys.add(key);

    const geo = enrichment || {};
    threats.push({
      logId: log.id,
      ip: log.ip,
      user: log.user,
      timestamp: log.timestamp,
      deviceId: log.deviceId || '',
      type,
      severity: SEVERITY_MAP[type] || 'medium',
      status: 'investigating',
      details: {
        ...details,
        ipCountry: geo.country,
        ipCity: geo.city,
        ipIsp: geo.isp,
        ipAsn: geo.asnName,
      },
      detectedAt: new Date().toISOString(),
    });
  };

  const groupedByIP = {};
  for (const log of logs) {
    if (!groupedByIP[log.ip]) groupedByIP[log.ip] = [];
    groupedByIP[log.ip].push(log);
  }

  for (const [ip, ipLogs] of Object.entries(groupedByIP)) {
    const enrichment = enrichMap.get(ip);
    const greyNoiseData = enrichment?.threatIntel?.greynoise;
    const greyNoiseClass = greyNoiseData?.community?.classification || greyNoiseData?.enterprise?.classification || '';

    if (greyNoiseClass === 'malicious' || greyNoiseData?.enterprise?.noise === true) {
      for (const log of ipLogs) {
        addThreat(log, THREAT_TYPES.KNOWN_BAD_IP, enrichment, {
          source: 'GreyNoise',
          classification: greyNoiseClass,
          noise: greyNoiseData?.enterprise?.noise,
        });
      }
    }

    if (enrichment?.threatIntel?.abuseIPDB?.abuseConfidenceScore >= 50) {
      for (const log of ipLogs) {
        addThreat(log, THREAT_TYPES.KNOWN_BAD_IP, enrichment, {
          score: enrichment.threatIntel.abuseIPDB.abuseConfidenceScore,
          reports: enrichment.threatIntel.abuseIPDB.totalReports,
          source: 'AbuseIPDB',
        });
      }
    }

    if (enrichment?.threatIntel?.alienVault?.pulseCount > 0) {
      for (const log of ipLogs) {
        addThreat(log, THREAT_TYPES.KNOWN_BAD_IP, enrichment, {
          pulses: enrichment.threatIntel.alienVault.pulseCount,
          source: 'AlienVault OTX',
        });
      }
    }

    const failures = ipLogs.filter(l => l.result && l.result.includes('Failure'));
    if (failures.length >= 5) {
      const timeRange = getTimeRange(failures);
      if (timeRange <= 300) {
        for (const log of failures) {
          addThreat(log, THREAT_TYPES.FAILURE_SPIKE, enrichment, {
            failureCount: failures.length,
            timeWindowMinutes: Math.round(timeRange / 60),
          });
        }
      }
    }

    if (failures.length >= 20) {
      for (const log of failures) {
        addThreat(log, THREAT_TYPES.BRUTE_FORCE, enrichment, {
          failureCount: failures.length,
        });
      }
    }
  }

  const groupedByUser = {};
  for (const log of logs) {
    if (!groupedByUser[log.user]) groupedByUser[log.user] = [];
    groupedByUser[log.user].push(log);
  }

  for (const [user, userLogs] of Object.entries(groupedByUser)) {
    userLogs.sort((a, b) => a._ts - b._ts);

    const userEnrichments = [];
    for (const log of userLogs) {
      const enrichment = enrichMap.get(log.ip);
      if (enrichment && enrichment.lat) {
        userEnrichments.push({ log, enrichment });
      }
    }

    if (userEnrichments.length >= 2) {
      for (let i = 0; i < userEnrichments.length - 1; i++) {
        const a = userEnrichments[i];
        const aTime = a.log._ts;
        for (let j = i + 1; j < userEnrichments.length; j++) {
          const b = userEnrichments[j];
          const timeDiff = Math.abs(b.log._ts - aTime) / 1000;

          if (timeDiff > 3600) break;

          if (timeDiff > 0) {
            const dist = haversineDistance(
              a.enrichment.lat, a.enrichment.lon,
              b.enrichment.lat, b.enrichment.lon
            );
            if (dist > 500) {
              addThreat(b.log, THREAT_TYPES.IMPOSSIBLE_TRAVEL, b.enrichment, {
                fromIP: a.log.ip,
                fromLocation: `${a.enrichment.city}, ${a.enrichment.country}`,
                toLocation: `${b.enrichment.city}, ${b.enrichment.country}`,
                distanceKm: Math.round(dist),
                timeDiffMinutes: Math.round(timeDiff / 60),
              });
            }
          }
        }
      }
    }

    const countries = new Set();
    for (const log of userLogs) {
      const enrichment = enrichMap.get(log.ip);
      if (enrichment?.country && !countries.has(enrichment.country)) {
        if (countries.size > 0) {
          addThreat(log, THREAT_TYPES.NEW_COUNTRY, enrichment, {
            country: enrichment.country,
            knownCountries: [...countries],
          });
        }
        countries.add(enrichment.country);
      }
    }

    for (const log of userLogs) {
      const hour = new Date(log._ts).getUTCHours();
      if (hour >= 0 && hour <= 5) {
        const enrich = enrichMap.get(log.ip);
        addThreat(log, THREAT_TYPES.ANOMALOUS_TIME, enrich, {
          hour,
          utcTime: new Date(log._ts).toISOString(),
        });
      }
    }
  }

  for (const log of logs) {
    const clientApp = (log.clientApp || '').toLowerCase();
    if (clientApp.includes('imap') || clientApp.includes('pop3') || clientApp.includes('smtp') || clientApp.includes('legacy') || clientApp.includes('pop')) {
      const enrich = enrichMap.get(log.ip);
      addThreat(log, THREAT_TYPES.LEGACY_AUTH, enrich, {
        clientApp: log.clientApp,
      });
    }
  }

  return threats;
}

function getTimeRange(logs) {
  if (logs.length < 2) return 0;
  let min = Infinity;
  let max = -Infinity;
  for (const l of logs) {
    const t = l._ts;
    if (!isNaN(t)) {
      if (t < min) min = t;
      if (t > max) max = t;
    }
  }
  if (!isFinite(min)) return 0;
  return (max - min) / 1000;
}